import { z } from 'zod'

/**
 * Client-side subscriber-session handoff (Section 15.1). The subscriber session
 * itself is an HttpOnly cookie the browser never reads; only the session-bound
 * CSRF token is exposed to the page, to be echoed as `X-MIST-CSRF` on giveaway
 * mutations (double-submit pattern). We keep it in sessionStorage — never a
 * cookie, never localStorage — so it clears with the tab and carries no PII.
 */
const HANDOFF_KEY = 'mist.subscriber.handoff'

const HandoffSchema = z.object({
  subscriberPublicId: z.string().min(1),
  csrfToken: z.string().min(1),
})

export type SubscriberHandoff = z.infer<typeof HandoffSchema>

export function storeSubscriberHandoff(handoff: SubscriberHandoff): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(handoff))
}

export function readSubscriberHandoff(): SubscriberHandoff | null {
  if (typeof window === 'undefined') return null
  const raw = window.sessionStorage.getItem(HANDOFF_KEY)
  if (!raw) return null
  try {
    return HandoffSchema.parse(JSON.parse(raw))
  } catch {
    return null
  }
}

export function clearSubscriberHandoff(): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(HANDOFF_KEY)
}
